import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { customFetch } from "../apiClient";
import QuillEditor from "../components/QuillEditor";
import UsgReceiptPrint from "../components/UsgReceiptPrint";

// Compact UI Select Styles
const selectStyles = {
  control: (b, s) => ({
    ...b, minHeight: 36, height: 36, fontSize: 12, borderRadius: 6,
    borderColor: s.isFocused ? "#0ea5e9" : "#cbd5e0",
    boxShadow: s.isFocused ? "0 0 0 2px rgba(14,165,233,.12)" : "none",
    "&:hover": { borderColor: "#0ea5e9" },
  }),
  valueContainer: (b) => ({ ...b, padding: "0 10px", height: 34 }),
  indicatorsContainer: (b) => ({ ...b, height: 34 }),
  dropdownIndicator: (b) => ({ ...b, padding: "0 6px" }),
  clearIndicator: (b) => ({ ...b, padding: "0 6px" }),
  input: (b) => ({ ...b, margin: 0, padding: 0, fontSize: 12 }),
  menu: (b) => ({ ...b, zIndex: 99999, borderRadius: 6, marginTop: 4, boxShadow: "0 10px 25px rgba(0,0,0,.1)", border: "1px solid #e2e8f0" }),
  menuPortal: (b) => ({ ...b, zIndex: 99999 }),
  option: (b, s) => ({
    ...b, fontSize: 12, padding: "8px 12px",
    backgroundColor: s.isSelected ? "#0ea5e9" : s.isFocused ? "#f1f5f9" : "white",
    color: s.isSelected ? "white" : "#0f172a",
    cursor: "pointer",
  }),
  placeholder: (b) => ({ ...b, color: "#94a3b8", fontSize: 12 }),
};

export default function UsgReportPage() {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  
  const [reportContent, setReportContent] = useState("");
  const [remarks, setRemarks] = useState(""); // ✅ Remarks state added

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  const [savedRecord, setSavedRecord] = useState(null);
  const [showPrint, setShowPrint] = useState(false);
  const [companyDetails, setCompanyDetails] = useState({});


  const formRef = useRef(null);

  useEffect(() => {
    const hospitalId = sessionStorage.getItem("hospitalId");
    if (hospitalId) {
      customFetch(`/Company/${hospitalId}`)
        .then(r => r.json()).then(d => setCompanyDetails(d || {})).catch(() => {});
    }
    customFetch(`/UsgMaster?activeOnly=true`)
      .then(r => r.json())
      .then(d => setTests(d.map(t => ({ label: t.testName, value: t.id, originalData: t }))))
      .catch(() => {});
  }, []);

  useEffect(() => { if (success) { const t = setTimeout(() => setSuccess(""), 3000); return () => clearTimeout(t); } }, [success]);
  useEffect(() => { if (error)   { const t = setTimeout(() => setError(""),   4000); return () => clearTimeout(t); } }, [error]);

  const loadPatientOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return [];
    try {
      const [ipdData, opdResult] = await Promise.all([
        customFetch(`/Ipds/list?search=${encodeURIComponent(inputValue)}`).then(r => r.json()).catch(() => []),
        customFetch(`/Opds/list?search=${encodeURIComponent(inputValue)}&useDateFilter=false`).then(r => r.json()).catch(() => ({ data: [] })),
      ]);

      const opdData = opdResult?.data || [];

      const ipdOptions = (Array.isArray(ipdData) ? ipdData : []).map(p => ({
        label: `[IPD] ${p.name} | UHID: ${p.uhiD_No || p.uhidNo} | Mob: ${p.mobileNo}`,
        value: p.uhiD_No || p.uhidNo,
        originalData: { ...p, type: "IPD", refId: p.ipD_ID },
      }));
      const opdOptions = (Array.isArray(opdData) ? opdData : []).map(p => ({
        label: `[OPD] ${p.name} | UHID: ${p.uhidNo || p.uhiD_No} | Mob: ${p.mobileNo}`,
        value: p.uhidNo || p.uhiD_No,
        originalData: { ...p, type: "OPD", refId: p.opdId },
      }));
      return [...ipdOptions, ...opdOptions];
    } catch { return []; }
  };


  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      
      // React-Select dropdown ya Quill Editor ke andar Enter dabane ko ignore karein
      if (e.target.closest('.react-select__menu') || e.target.closest('.ql-editor')) return;

      const focusableSelector = 
        'input:not([disabled]):not([type="hidden"]):not([readonly]), ' +
        'select:not([disabled]), ' +
        'textarea:not([disabled]):not([readonly]), ' +
        'button:not([disabled])';

      const focusableElements = Array.from(
        formRef.current?.querySelectorAll(focusableSelector) || []
      );

      if (focusableElements.length === 0) return;
      const currentIndex = focusableElements.indexOf(document.activeElement);

      if (currentIndex >= 0 && currentIndex < focusableElements.length - 1) {
        e.preventDefault();
        focusableElements[currentIndex + 1]?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleTestSelect = (opt) => {
    setSelectedTest(opt);
    setReportContent(opt?.originalData?.testContent || "");
  };

  const handleSave = async () => {
    setError(""); setSuccess("");
    if (!selectedPatient) return setError("Please select a patient first.");
    if (!reportContent)   return setError("Please write or load a report content.");

    setSaving(true);
    try {
      const p = selectedPatient.originalData;
      const sourceType = p.type || "OPD";

      const body = {
        opdId: sourceType === "OPD" ? (p.refId || null) : null,
        ipdId: sourceType === "IPD" ? (p.refId || null) : null,
        sourceType,
        uhid_No: selectedPatient.value,
        patientName: p.name || "",
        mobileNo: p.mobileNo || "",
        age: p.age != null ? String(p.age) : "",
        ageIn: p.ageIn != null ? String(p.ageIn) : "",
        gender: p.gender || "",
        address: p.address || "",
        city: p.city || "",
        stateName: p.stateName || "",
        pincode: p.pincode || "",
        emailId: p.emailId || "",
        doctorId: p.doctorId || null,
        doctorName: p.doctorName || "",
        departmentId: p.departmentId || null,
        departmentName: p.departmentName || "",
        testMasterId: selectedTest?.value || null,
        testName: selectedTest?.label || "Diagnostic Report",
        testContent: reportContent,
        reportDate: new Date().toISOString().split("T")[0],
        remarks: remarks.trim(), // ✅ Sending remarks to backend
        amount: 0, payMode: "None", received: 0, dues: 0,
      };

      const r = await customFetch(`/UsgDt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (r.ok || r.status === 201) {
        const saved = await r.json();
        setSavedRecord({ ...body, id: saved.id, hid_SrNo: saved.hid_SrNo });
        setSuccess("Report saved successfully!");
        setShowPrint(true);
      } else {
        setError(`Save failed: ${await r.text()}`);
      }
    } catch (ex) { setError(`Error: ${ex.message}`); }
    finally { setSaving(false); }
  };

  const handleReset = () => {
    setSelectedPatient(null); setSelectedTest(null);
    setReportContent(""); setRemarks(""); // ✅ Clear remarks on reset
    setSavedRecord(null);
    setError(""); setSuccess("");
  };

  return (
    <div className="advance-form-container compact-ui">
      
      {/* 1. Main Background UI - Gets hidden when printing */}
      <div className="advance-card no-print">
        
        {/* Header */}
        <div className="advance-header">
          <div className="header-left">
            <div className="header-icon"><i className="bi bi-file-medical-fill"></i></div>
            <div className="header-text">
              <h5>Diagnostic Report Generation</h5>
              <p>Create and print patient diagnostic reports</p>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert-custom alert-danger slide-in">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
            <button className="alert-close" onClick={() => setError("")}><i className="bi bi-x"></i></button>
          </div>
        )}
        {success && (
          <div className="alert-custom alert-success slide-in">
            <i className="bi bi-check-circle-fill"></i>
            <span>{success}</span>
            <button className="alert-close" onClick={() => setSuccess("")}><i className="bi bi-x"></i></button>
          </div>
        )}

        <div className="main-content">
          <div className="form-panel" ref={formRef}>
            
            {/* Top Selection Section */}
            <div className="form-section mb-3">
              <div className="section-title">
                <i className="bi bi-person-lines-fill"></i>
                <span>Patient & Template Selection</span>
              </div>
              <div className="section-content">
                <div className="form-row two-col">
                  
                  <div className="form-group">
                    <label>Search Patient <span className="required">*</span></label>
                    <AsyncSelect
                      autoFocus cacheOptions
                      loadOptions={loadPatientOptions}
                      onChange={setSelectedPatient}
                      value={selectedPatient}
                      placeholder="Type Name, UHID or Mobile..."
                      isClearable styles={selectStyles}
                      menuPortalTarget={document.body}
                    />
                    {selectedPatient && (
                      <div className="mt-2 p-2 rounded" style={{background: '#f0f9ff', border: '1px solid #bae6fd', fontSize: '11px', color: '#0369a1'}}>
                        <strong>{selectedPatient.originalData.name} ({selectedPatient.originalData.age}/{selectedPatient.originalData.gender})</strong>
                        <span className="float-end">UHID: {selectedPatient.value} | {selectedPatient.originalData.type}</span>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Select Format Template</label>
                    <Select
                      options={tests}
                      onChange={handleTestSelect}
                      value={selectedTest}
                      placeholder="Search and load a template..."
                      isClearable styles={selectStyles}
                      menuPortalTarget={document.body}
                    />
                    {selectedTest && (
                      <div className="mt-2 p-2 rounded" style={{background: '#ecfdf5', border: '1px solid #a7f3d0', fontSize: '11px', color: '#047857'}}>
                        <i className="bi bi-check-circle-fill me-1" />
                        <strong>{selectedTest.label} loaded</strong>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            </div>

            {/* Editor Section */}
            <div className="form-section">
              <div className="section-title">
                <i className="bi bi-pen-fill"></i>
                <span>Edit Report Content</span>
              </div>
              <div className="section-content p-0" style={{ borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', overflow: 'hidden' }}>
                <QuillEditor
                  value={reportContent}
                  onChange={setReportContent}
                  minHeight="350px"
                />
                
                {/* ✅ Added Remarks Field neatly below the editor */}
                <div className="p-3 bg-light border-top">
                  <div className="form-group">
                    <label>Remarks (Optional)</label>
                    <input 
                      type="text" 
                      className="form-control-custom" 
                      value={remarks} 
                      onChange={(e) => setRemarks(e.target.value)} 
                      placeholder="Enter any additional remarks or notes..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="form-actions mt-3">
              <button className="btn-secondary" onClick={handleReset} disabled={saving}>
                <i className="bi bi-arrow-counterclockwise"></i> Clear Data
              </button>
              <button className="btn-primary px-4" onClick={handleSave} disabled={saving || !selectedPatient || !reportContent}>
                {saving ? "Saving..." : <><i className="bi bi-printer-fill"></i> Save & Print Report</>}
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* 2. Print Modal (Renders on top of everything) */}
      {showPrint && savedRecord && (
        <UsgReceiptPrint
          show={showPrint}
          data={savedRecord}
          companyDetails={companyDetails}
          onClose={() => { setShowPrint(false); handleReset(); }}
        />
      )}

      {/* CSS matching the compact-ui theme from Master/Advance */}
      <style>{`
        .compact-ui { font-family: system-ui, -apple-system, sans-serif; }
        .advance-form-container { padding: 0; max-height: 100vh; overflow-y: auto; background: #f8fafc; min-height: 100vh; padding-bottom: 40px; }
        .advance-card { background: white; border-radius: 8px; box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05); border: 1px solid #e0e6ed; overflow: visible; margin: 15px; }
        
        .advance-header { background: var(--theme-color, #0088cc); padding: 10px 16px; display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #00d4aa; border-top-left-radius: 8px; border-top-right-radius: 8px; }
        .header-left { display: flex; align-items: center; gap: 10px; }
        .header-icon { width: 34px; height: 34px; background: rgba(255, 255, 255, 0.2); border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 16px; color: white; }
        .header-text h5 { margin: 0; color: white; font-size: 15px; font-weight: 600; line-height: 1.1; }
        .header-text p { margin: 0; color: rgba(255, 255, 255, 0.85); font-size: 10.5px; }

        .main-content { display: flex; flex-direction: column; gap: 12px; padding: 16px; }
        .form-panel { width: 100%; }
        .form-section { background: #ffffff; border: 1px solid #e0e6ed; border-radius: 8px; overflow: visible; }
        
        .section-title { background: #f0f4f8; padding: 8px 12px; display: flex; align-items: center; gap: 6px; border-bottom: 2px solid var(--theme-color, #0088cc); border-top-left-radius: 8px; border-top-right-radius: 8px; }
        .section-title i { font-size: 13px; color: var(--theme-color, #0088cc); }
        .section-title span { font-size: 12px; font-weight: 600; color: #2c3e50; flex: 1; }
        .section-content { padding: 16px; background: white; border-bottom-left-radius: 8px; border-bottom-right-radius: 8px;}
        
        .form-row.two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .form-group { display: flex; flex-direction: column; gap: 5px; }
        .form-group label { font-size: 11px; font-weight: 600; color: #2c3e50; display: flex; align-items: center; gap: 4px; }
        .required { color: #e74c3c; }

        .form-actions { display: flex; justify-content: flex-end; gap: 8px; }
        .btn-primary, .btn-secondary { padding: 8px 18px; border-radius: 6px; font-size: 12px; font-weight: 600; border: none; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; gap: 5px; }
        .btn-primary { background: var(--theme-color, #0088cc); color: white; }
        .btn-primary:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
        .btn-secondary { background: #e0e6ed; color: #2c3e50; }
        .btn-secondary:hover:not(:disabled) { background: #cbd5e0; transform: translateY(-1px); }
        .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

        .alert-custom { margin: 12px 16px 0; padding: 10px 14px; border-radius: 6px; display: flex; align-items: center; gap: 8px; font-size: 11px; font-weight: 600; position: relative; }
        .alert-danger { background: #fee2e2; color: #b91c1c; border: 1px solid #fecaca; }
        .alert-success { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }
        .alert-close { position: absolute; right: 8px; background: transparent; border: none; cursor: pointer; opacity: 0.6; }

        /* Bootstrap utility class emulation for the remarks section */
        .bg-light { background-color: #f8f9fa !important; }
        .border-top { border-top: 1px solid #e2e8f0 !important; }
        .p-3 { padding: 1rem !important; }

        @media (max-width: 768px) { .form-row.two-col { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
}